import { atom } from 'jotai';
import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';

import { withImageOptimizationQuery } from '@common/utils';

import {
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_matched_item_list as Item,
  PdpCatalogItemDisplayStatus,
  PdpCatalogSalesStatus,
  PdpDeliveryType,
} from '../graphql/select-option_types.2';
import {
  CustomOptionValueList,
  CustomTextOption,
  ItemCardType,
  ItemListMapValueType,
  OptionType,
  OptionValue,
  SelectedOption,
} from '../types/option-type';
import { initOptions } from './utils/init';
import {
  ActiveIndexAtom,
  ActiveParentIndexAtom,
  AdditionalOptionsAtom,
  BogoRequiredQuantityAtom,
  IsProductTypeZigzinAtom,
  ItemsAtom,
  MachingOptionAtom,
  MaximumCountAtom,
  OnlyZigzinOptionAtom,
  OptionsAtom,
  OptionSourceDataAtom,
  OriginOptionDataAtom,
  ProductIdAtom,
  ProductItemListAtom,
  SelectedOptionRestockNotiInfoAtom,
  TextOptionsAtom,
} from './baseAtoms';
import { findOptionInAllOption, getItemOptions, isEventProduct } from './util';

const CurrentOptionItemsAtom = atom((get) => {
  const options = get(OptionsAtom);
  const activeParentIndex = get(ActiveParentIndexAtom);
  return options?.[activeParentIndex] ?? [];
});

const SliceOptionItemsAtom = atom((get) => {
  const activeIndex = get(ActiveIndexAtom);
  const currentOptionItems = get(CurrentOptionItemsAtom);
  return currentOptionItems?.slice(0, activeIndex) ?? [];
});

const IsZigzinInlastOptionAtom = atom((get) => {
  const sliceOptionItems = get(SliceOptionItemsAtom);
  const sliceOptionItemsLength = sliceOptionItems?.length ?? 0;
  const sliceOptionItemsLastIndex = sliceOptionItemsLength > 0 ? sliceOptionItemsLength - 1 : 0;
  return sliceOptionItems[sliceOptionItemsLastIndex]?.is_zigzin;
});

const OptionSelectedValueIdListAtom = atom((get) => {
  const sliceOptionItems = get(SliceOptionItemsAtom);
  const isZigzinInlastOption = get(IsZigzinInlastOptionAtom);

  return sliceOptionItems.reduce((acc: string[], cur) => {
    const selectedOption = cur.value_list
      .filter((o) => (isZigzinInlastOption ? o.is_zigzin : !o.is_zigzin))
      .find((value) => value.value === cur.selected_value);

    if (selectedOption) {
      acc.push(selectedOption.id);
    }
    return acc;
  }, []);
});

const OppositeTypeOptionsIdsAtom = atom((get) => {
  const matchingOption = get(MachingOptionAtom);
  const sliceOptionItems = get(SliceOptionItemsAtom);
  const isZigzinInlastOption = get(IsZigzinInlastOptionAtom);

  const ids: string[] = [];

  if (matchingOption) {
    sliceOptionItems.forEach((o) => {
      const oppositeTypeValue = o.value_list
        .filter((o) => (isZigzinInlastOption ? !o.is_zigzin : o.is_zigzin))
        .find((value) => value.value === o.selected_value);

      if (oppositeTypeValue) {
        ids.push(oppositeTypeValue.id);
      }
    });
  }

  return ids;
});

/**
 * 아이템 목록을 Map으로 관리합니다.
 * ex) 123456;1234567;12345678: Item
 */
export const ItemListMapAtom = atom((get) => {
  const itemList = get(ProductItemListAtom);
  const itemListMap: Map<string, ItemListMapValueType> = new Map();

  itemList.forEach((item) => {
    const {
      item_attribute_list,
      display_status,
      sales_status,
      final_price,
      price,
      remain_stock,
      delivery_type,
      expected_delivery_date,
    } = item;
    const ids = item_attribute_list.map((o) => o.value_id);
    const id = ids.join(';');
    const firstId = ids[0];
    const firstMatchedItem = itemListMap.get(firstId);
    const currentItem: ItemListMapValueType = {
      display_status,
      sales_status,
      final_price,
      remain_stock,
      delivery_type,
      expected_delivery_date,
      price,
    };

    const updateItemStatus = (
      currentItem: ItemListMapValueType,
      matchedItem: ItemListMapValueType,
      statusKey: 'display_status' | 'sales_status' | 'delivery_type',
      statusVisible: PdpCatalogItemDisplayStatus | PdpCatalogSalesStatus | PdpDeliveryType,
    ) => {
      if (matchedItem[statusKey] !== statusVisible && currentItem[statusKey] === statusVisible) {
        return true;
      }
      return false;
    };

    itemListMap.set(id, currentItem);

    /**
     * 첫번째 조건에 대한 상태 판별 조건을 미리 저장해둔다.
     */
    if (firstMatchedItem) {
      const displayStatusChanged = updateItemStatus(
        currentItem,
        firstMatchedItem,
        'display_status',
        PdpCatalogItemDisplayStatus.VISIBLE,
      );
      const salesStatusChanged = updateItemStatus(
        currentItem,
        firstMatchedItem,
        'sales_status',
        PdpCatalogSalesStatus.ON_SALE,
      );

      const delivertTypeStatusChanged = updateItemStatus(
        currentItem,
        firstMatchedItem,
        'delivery_type',
        PdpDeliveryType.FAST,
      );

      if (displayStatusChanged || salesStatusChanged || delivertTypeStatusChanged) {
        itemListMap.set(firstId, currentItem);
      }
    } else {
      itemListMap.set(firstId, currentItem);
    }
  });

  return itemListMap;
});

export const IsNoOptionAtom = atom((get) => {
  const originOptionsData = get(OriginOptionDataAtom);
  const customInputOptions = get(TextOptionsAtom);
  const bogo_required_quantity = get(BogoRequiredQuantityAtom);

  if (!originOptionsData || !customInputOptions) {
    return true;
  }

  const originOptions = initOptions(originOptionsData.product_option_list, bogo_required_quantity);
  return !flatten(originOptions).length && !flatten(customInputOptions).length;
});

export const ItemCardsAtom = atom((get) => {
  const items = get(ItemsAtom);
  const id = get(ProductIdAtom);
  const itemValues = Object.values(items);
  const bogo_required_quantity = get(BogoRequiredQuantityAtom);
  const maxCount = get(MaximumCountAtom);

  function getOptionName({
    options,
    text_options,
    origin_name,
    use_origin,
  }: {
    options: SelectedOption[][];
    text_options: CustomTextOption[][];
    origin_name: string;
    use_origin: boolean;
  }) {
    try {
      if (use_origin) return origin_name;
      let combined_name = compact(
        options.map((nested_options, i) => {
          const option_str = compact(nested_options.map((o) => o.selected_value)).join(' / ');
          const text_option_str = compact(text_options[i].map((o) => o.selected_value)).join(' / ');

          return compact([option_str, text_option_str]).join(' / ');
        }),
      );

      if (combined_name.length === 0) {
        combined_name = compact(
          text_options.map((current_text_option) => {
            return current_text_option.map((o) => o.selected_value).join('');
          }),
        );
      }

      if (combined_name.length === 0) {
        throw new Error('아이템 이름이 존재하지 않습니다.');
      }

      return combined_name.join(' , ');
    } catch (err) {
      return '기본옵션';
    }
  }

  return itemValues.map<ItemCardType>((item) => {
    const isAdditionalOption = item.option_type === OptionType.ADDITIONAL;
    const optionNames = getOptionName({
      options: item.options,
      text_options: item.text_options,
      origin_name: item.name,
      use_origin: isAdditionalOption,
    });

    let is_zigzin;
    if (isAdditionalOption) {
      is_zigzin = Boolean(item.is_zigzin);
    } else {
      const selectedOptionShippingTypeList = flatten(item.options).map(
        (o) => o.value_list.find((o2) => o2.id === o.selected_value_id)?.is_zigzin,
      );

      const selectedOptionShippingTypeListLength = selectedOptionShippingTypeList.length;
      const lastSelectedOptionIndex =
        selectedOptionShippingTypeListLength > 0 ? selectedOptionShippingTypeListLength - 1 : 0;

      is_zigzin = Boolean(selectedOptionShippingTypeList[lastSelectedOptionIndex]);
    }

    const is_bogo = item.origin_items.length > 1;

    return {
      item,
      disableCounter: isEventProduct(id),
      optionNames,
      minCount: 1,
      maxCount,
      count: item.count,
      price_delta: item.price_delta,
      is_bogo,
      is_zigzin,
      product_id: id,
      ...(is_bogo && { bogo_required_quantity }),
    };
  });
});

export const hasAdditionalOptionsAtom = atom((get) => {
  const items = get(AdditionalOptionsAtom) ?? [];

  return items.length > 0;
});

export const HasFastShippingOptionAtom = atom((get) => {
  const itemList = get(ProductItemListAtom) ?? [];
  return Boolean(itemList.find((o) => o.delivery_type === PdpDeliveryType.FAST));
});

export const HasAdditionalFastShippingOptionAtom = atom((get) => {
  const options = get(AdditionalOptionsAtom) ?? [];
  return Boolean(options.find((o) => o.item_list.find((o2) => o2.delivery_type === PdpDeliveryType.FAST)));
});

export const HasSelectedItemAtom = atom((get) => get(ItemCardsAtom).length > 0);

export const OptionMatchedItemsAtom = atom((get) => {
  const options = get(OptionsAtom);
  const itemList = get(ProductItemListAtom);
  const matchingOption = get(MachingOptionAtom);
  const activeParentIndex = get(ActiveParentIndexAtom);
  const optionListLength = options[activeParentIndex]?.length ?? 0;
  const lastOptionIndex = optionListLength - 1;

  return compact(
    options.map((option_items) => {
      const selectedOption = option_items.filter((option) => option.selected_value);

      const selectedIds: string[] = [];
      const selectedNames: string[] = [];

      selectedOption.forEach(({ selected_value_id, value_list }) => {
        const selectedItem = value_list.find(({ id }) => id === selected_value_id);

        if (selectedItem) {
          selectedIds.push(selectedItem?.id);
          selectedNames.push(selectedItem?.value);
        }
      });

      const optionItem = itemList.find((item) => {
        const valueIds = item.item_attribute_list.map((attribute) => attribute.value_id);
        return isEqual(valueIds, selectedIds);
      });

      const matchingOptionItem =
        matchingOption &&
        optionListLength === selectedOption.length &&
        findOptionInAllOption(itemList ?? [], '', selectedNames.join('/'), lastOptionIndex);

      return optionItem || matchingOptionItem;
    }),
  );
});

export const OptionValuesAtom = atom((get) => {
  const itemListMap = get(ItemListMapAtom);
  const [zigzinItemList, generalItemList] = get(ItemListByShippingTypeAtom);
  const bogo_required_quantity = get(BogoRequiredQuantityAtom);
  const activeParentIndex = get(ActiveParentIndexAtom);
  const activeIndex = get(ActiveIndexAtom);

  const optionSourceData = get(OptionSourceDataAtom);
  const originOptionsData = get(OriginOptionDataAtom);
  const options = get(OptionsAtom);
  const itemList = get(ProductItemListAtom);
  const selectedOptionRestockNotiInfo = get(SelectedOptionRestockNotiInfoAtom);

  const checkOnlyZigzigOption = get(OnlyZigzinOptionAtom);
  const matchingOption = get(MachingOptionAtom);
  const isProductTypeZigzin = get(IsProductTypeZigzinAtom);
  const currentOptionItems = get(CurrentOptionItemsAtom);
  const sliceOptionItems = get(SliceOptionItemsAtom);
  const oppositeTypeOptionsIds = get(OppositeTypeOptionsIdsAtom);
  const optionSelectedValueIdList = get(OptionSelectedValueIdListAtom);

  if (!originOptionsData) {
    return [];
  }

  const originOptions = initOptions(originOptionsData.product_option_list, bogo_required_quantity);
  const activeOption = originOptions[activeParentIndex]?.[activeIndex];

  const currentOptionsLength = originOptions[activeParentIndex]?.length ?? 0;

  if (!activeOption || activeIndex >= currentOptionsLength) {
    return [] as OptionValue[];
  }

  const selectedValue = currentOptionItems
    .reduce((acc: string[], o) => {
      if (o.selected_value) {
        acc.push(o.selected_value);
      }
      return acc;
    }, [])
    .join('/');

  const lastOptionIndex = currentOptionItems?.length ? currentOptionItems?.length - 1 : 0;
  const isFirstOptionDepth = activeIndex === 0;
  const isLastOptionDepth = lastOptionIndex === activeIndex;

  const isMatchingZigzinProduct = isProductTypeZigzin && matchingOption;
  const isMatchingGeneralProduct = !isProductTypeZigzin && Boolean(matchingOption);
  const zigzinOnSaleOptionList = getZigzinOnSaleOptionList();

  if (checkOnlyZigzigOption) {
    activeOption.value_list = activeOption.value_list.filter((o) => o.is_zigzin);
  }

  // 직진 매칭 상품이고 상위 옵션에서 직진 상품을 선택한 경우 일반 옵션 미노출
  if (isProductTypeZigzin && matchingOption && options[activeParentIndex]?.[0]?.is_zigzin) {
    activeOption.value_list = activeOption.value_list.filter((o) =>
      optionSourceData?.product_option_list[activeIndex].value_list.find((o2) => o.value === o2.value),
    );
  }

  function getZigzinOnSaleOptionList() {
    const optionList: CustomOptionValueList[] = [];
    activeOption.value_list
      .filter((o) => o.is_zigzin)
      .forEach((o) => {
        const onSale = !isSoldOut(o.id);
        if (onSale) {
          optionList.push(o);
        }
      });

    return optionList;
  }

  function getMatchedItem(joinedOptionKey: string, metaOptionKey: string) {
    let matchedItem = itemListMap.get(joinedOptionKey);

    if (!matchedItem && matchingOption && metaOptionKey) {
      matchedItem = itemListMap.get(metaOptionKey);
    }

    return matchedItem;
  }

  function getReplaceableOptions(value: string) {
    const replaceableGeneralOption = findOptionInAllOption(generalItemList, selectedValue, value, activeIndex, true);
    const replaceableZigzinOption = findOptionInAllOption(zigzinItemList, selectedValue, value, activeIndex, true);
    const hasZigzinOption = findOptionInAllOption(zigzinItemList, selectedValue, value, activeIndex);

    return {
      replaceableGeneralOption,
      replaceableZigzinOption,
      hasZigzinOption,
    };
  }

  function isSellable({ id, value, is_zigzin }: CustomOptionValueList) {
    try {
      if (isMatchingZigzinProduct && !checkOnlyZigzigOption) {
        const { replaceableGeneralOption, replaceableZigzinOption, hasZigzinOption } = getReplaceableOptions(value);
        if (is_zigzin && isSoldOut(id)) {
          // MEMO: 교체 가능한 일반 옵션이 있으면 직진 옵션 미노출
          if (replaceableGeneralOption) {
            return false;
          }

          // MEMO: 교체 가능한 일반 옵션이 없고 조합 가능한 직진 옵션이 있으면 노출
          if (!replaceableGeneralOption && hasZigzinOption) {
            return true;
          }
        }

        // MEMO: 직진 옵션 품절이 아닐 경우 일반 옵션 미노출
        if (!is_zigzin && replaceableZigzinOption) {
          return false;
        }

        // MEMO: 품절된 직진 옵션을 교체할 수 있는 일반 옵션이 있으면 노출
        if (!is_zigzin && replaceableGeneralOption) {
          return true;
        }
      }

      if (isMatchingGeneralProduct && !checkOnlyZigzigOption) {
        const { replaceableGeneralOption, replaceableZigzinOption } = getReplaceableOptions(value);
        const hideGeneralOption = !is_zigzin && replaceableZigzinOption;

        if (hideGeneralOption) {
          return false;
        }

        const replaceableZigzinOptioninLastZigzinOption = is_zigzin && isLastOptionDepth && replaceableZigzinOption;
        const replaceableZigzinOptioninNotLastZigzinOption =
          is_zigzin && !isLastOptionDepth && !replaceableGeneralOption && replaceableZigzinOption;

        // MEMO: 일반 옵션을 교체할 수 있는 직진 옵션이 있으면 노출 | 전체 조합 일반 옵션 중에 직진 옵션 대신하여 우선 노출할 일반 옵션이 없으면 품절이 아닌 직진 옵션 노출
        if (replaceableZigzinOptioninLastZigzinOption || replaceableZigzinOptioninNotLastZigzinOption) {
          return true;
        }

        if (!is_zigzin && !isFirstOptionDepth) {
          // MEMO: item_list에서 조합 가능한(판매 여부 상관 x) 일반 옵션이고 일반 옵션을 교체할 수 있는 직진 옵션이 없으면 일반 옵션 노출
          const findGeneralOption = findOptionInAllOption(generalItemList, selectedValue, value, activeIndex);
          const displayGeneralOption = findGeneralOption && !zigzinOnSaleOptionList.find((o) => o.value === value);

          if (displayGeneralOption) {
            return true;
          }

          const hideGeneralOption = !replaceableGeneralOption && replaceableZigzinOption;

          // MEMO: 교체해서 보여줄 직진 옵션이 있으면 일반 옵션 미노출 | 일반 옵션이 품절이고 교체할 직진 옵션이 있을 경우 일반 옵션 미노출
          if (replaceableZigzinOption || hideGeneralOption) {
            return false;
          }
        }
      }

      const { metaOptions, joinedOptions, length } = getStackedOptionsWithCurrent(id);

      if (length !== 1 && length < currentOptionsLength) {
        return itemList.some((item) => {
          const itemOptions = getItemOptions(item.item_attribute_list, length);
          return (
            (joinedOptions === itemOptions || (matchingOption && metaOptions === itemOptions)) &&
            item.display_status === PdpCatalogItemDisplayStatus.VISIBLE
          );
        });
      }

      return getMatchedItem(joinedOptions, metaOptions)?.display_status === PdpCatalogItemDisplayStatus.VISIBLE;
    } catch (e) {
      console.log(e);
    }
  }

  function isSoldOut(value_id: string) {
    const { joinedOptions, metaOptions, length } = getStackedOptionsWithCurrent(value_id);

    if (length !== 1 && length < currentOptionsLength) {
      return itemList.reduce((memo, item) => {
        const itemOptions = getItemOptions(item.item_attribute_list, length);

        return joinedOptions === itemOptions || (matchingOption && metaOptions === itemOptions)
          ? memo && item.sales_status !== PdpCatalogSalesStatus.ON_SALE
          : memo;
      }, true);
    }

    return getMatchedItem(joinedOptions, metaOptions)?.sales_status !== PdpCatalogSalesStatus.ON_SALE;
  }

  function getStackedOptionsWithCurrent(current: string) {
    let addedOptions: string[] = [];

    sliceOptionItems.forEach((o) => {
      if (o.selected_value_id) {
        addedOptions.push(o.selected_value_id);
      }
    });
    addedOptions = matchingOption ? optionSelectedValueIdList.concat(current) : addedOptions.concat(current);

    const joinedOptions = addedOptions.join(';');
    const clone_options = cloneDeep(addedOptions);

    // MEMO: 선택한 옵션과 같은 이름을 가진 반대 배송 타입 상품 id로 교체
    oppositeTypeOptionsIds.forEach((id, idx) => {
      if (id) {
        clone_options[idx] = id;
      }
    });

    const metaOptions = clone_options.join(';');

    return {
      metaOptions,
      joinedOptions,
      length: addedOptions.length,
    };
  }

  function getDeliveryType(value_id: string, value: string, { requiredOnSale }: { requiredOnSale: boolean }) {
    let item;
    if (isFirstOptionDepth || isLastOptionDepth) {
      const { joinedOptions, metaOptions } = getStackedOptionsWithCurrent(value_id);
      item = itemListMap.get(joinedOptions) || itemListMap.get(metaOptions);
    } else {
      item = findOptionInAllOption(generalItemList, selectedValue, value, activeIndex, requiredOnSale);
    }
    return item?.delivery_type;
  }

  function getNotificationInfo(value_id: string) {
    const { joinedOptions, metaOptions, length } = getStackedOptionsWithCurrent(value_id);

    const current_item = itemList.find((item) => {
      const itemOptions = getItemOptions(item.item_attribute_list, length);
      return joinedOptions === itemOptions || (matchingOption && metaOptions === itemOptions);
    });

    const restockNotiActiveStatus =
      selectedOptionRestockNotiInfo.product_item_id === current_item?.id
        ? selectedOptionRestockNotiInfo.active
        : (current_item?.wms_notification_info?.active ?? false);

    return current_item
      ? {
          product_item_id: current_item.id,
          active: restockNotiActiveStatus,
        }
      : null;
  }

  function setOptionValueWithItemList(optionValues: OptionValue[]) {
    if (!isLastOptionDepth) {
      return;
    }

    const selectedOption = currentOptionItems.filter((option) => option.selected_value);

    const [selectedIds, selectedNames]: [string[], string[]] = [[], []];

    selectedOption.forEach(({ selected_value_id, value_list }) => {
      const selectedItem = value_list.find(({ id }) => id === selected_value_id);

      if (selectedItem) {
        selectedIds.push(selectedItem?.id);
        selectedNames.push(selectedItem?.value);
      }
    });

    optionValues.forEach((value) => {
      const optionSelectedValueIdListKey = optionSelectedValueIdList.concat(value.id).join(';');
      const oppositeTypeOptionsIdsKey = oppositeTypeOptionsIds.concat(value.id).join(';');
      const matchedItem = itemListMap.get(optionSelectedValueIdListKey) ?? itemListMap.get(oppositeTypeOptionsIdsKey);

      if (matchedItem) {
        value.origin_price = matchedItem?.price;
        value.price = matchedItem?.final_price;
        value.remain_stock = matchedItem?.remain_stock ?? null;
      }
    });
  }

  const optionValues = activeOption.value_list.filter(isSellable).map<OptionValue>((value) => {
    const sold_out = isSoldOut(value.id);

    return {
      ...value,
      image_url: withImageOptimizationQuery(value.jpeg_url, { width: 120, height: 120 }),
      webp_image_url: withImageOptimizationQuery(value.static_url, { width: 120, height: 120 }),
      sold_out,
      delivery_type: getDeliveryType(value.id, value.value, { requiredOnSale: true }) ?? null,
      ...(sold_out && { notification_info: getNotificationInfo(value.id) }),
    };
  });

  setOptionValueWithItemList(optionValues);

  return optionValues;
});

/**
 * 직진, 일반 배송타입 관리되는 itemList
 */
export const ItemListByShippingTypeAtom = atom((get) => {
  const itemList = get(ProductItemListAtom);
  return itemList.reduce<[Item[], Item[]]>(
    (acc, item) => {
      item.is_zigzin ? acc[0].push(item) : acc[1].push(item);
      return acc;
    },
    [[], []],
  );
});
